// FAQ page: fetches questions from the API and renders them grouped by category.
// Reads the current language from the <html lang="..."> attribute or
// the data-lang attribute on the #faq-content container.
import { escapeHtml } from '../shared/utils.ts'
import { getDocumentLanguage, t } from '../shared/i18n.ts'
import { api_v1_faqs_path } from '../shared/api-routes'

interface FaqEntry {
  category: string
  question: string
  answer: string
}

interface CategoryLabel {
  key: string
  fallback: string
}

const CATEGORY_LABELS: Record<string, CategoryLabel> = {
  general: { key: 'faq.generalTitle', fallback: 'General' },
  planning: { key: 'faq.planningTitle', fallback: 'Planning' },
  equipment: { key: 'faq.equipmentTitle', fallback: 'Equipment' },
  billing: { key: 'faq.billingTitle', fallback: 'Billing' },
  technical: { key: 'faq.technicalTitle', fallback: 'Technical' }
}

const CATEGORY_ORDER = ['general', 'planning', 'equipment', 'billing', 'technical']


export function initFaqPage(): void {
  const container = document.getElementById('faq-content')
  if (!container) return

  // If the server already rendered FAQ entries (blueprint SSR), skip
  // the async API hydration — SSR markup is authoritative for styling.
  if (container.querySelector('.faq-section')) return

  const lang = container.dataset.lang || getDocumentLanguage()

  fetch(api_v1_faqs_path() + '?lang=' + encodeURIComponent(lang))
    .then((res) => {
      // Without this the 500's HTML body blows up inside json(), so a
      // server fault was indistinguishable from a malformed payload.
      if (!res.ok) throw new Error(`FAQ request failed: HTTP ${res.status}`)
      return res.json()
    })
    .then((faqs: FaqEntry[]) => {
      if (!faqs || !faqs.length) {
        container.innerHTML = '<p data-i18n="faq.empty">No FAQs available.</p>'
        return
      }

      const grouped: Record<string, FaqEntry[]> = {}
      faqs.forEach((faq) => {
        if (!grouped[faq.category]) grouped[faq.category] = []
        grouped[faq.category].push(faq)
      })

      let html = ''
      CATEGORY_ORDER.forEach((cat) => {
        if (!grouped[cat]) return
        const label = CATEGORY_LABELS[cat] || { key: cat, fallback: cat }
        html += '<section class="legal-section">'
        html += '<h2 data-i18n="' + label.key + '">' + label.fallback + '</h2>'
        grouped[cat].forEach((faq) => {
          html += '<details>'
          html += '<summary>' + escapeHtml(faq.question) + '</summary>'
          html += '<p>' + escapeHtml(faq.answer) + '</p>'
          html += '</details>'
        })
        html += '</section>'
      })

      container.innerHTML = html

      const schemaEl = document.getElementById('faq-schema')
      if (schemaEl) {
        const schema = {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer
            }
          }))
        }
        const script = document.createElement('script')
        script.type = 'application/ld+json'
        script.textContent = JSON.stringify(schema)
        schemaEl.appendChild(script)
      }
    })
    .catch(() => {
      const p = document.createElement('p')
      p.textContent = t('faq.loadError')
      container.replaceChildren(p)
    })
}
